import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { muxAdminAPI } from '../services/api';
import AlertModal from '../components/common/AlertModal';

const LiveStreamAdmin = () => {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const [creating, setCreating] = useState(false);
  const [streamData, setStreamData] = useState(null);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });

  if (authLoading) {
    return (
      <div className="min-h-screen bg-battle-dark flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!user || !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  const handleCreateStream = async () => {
    setCreating(true);
    try {
      const response = await muxAdminAPI.createLiveStream({});
      setStreamData(response.data);
    } catch (error) {
      setAlertModal({
        show: true,
        title: 'Error',
        message: error.response?.data?.error || 'Failed to create live stream',
        type: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setAlertModal({
      show: true,
      title: 'Copied',
      message: `${label} copied to clipboard`,
      type: 'success',
    });
  };

  return (
    <div className="min-h-screen bg-battle-dark py-8">
      <div className="max-w-3xl mx-auto px-4">
        <h1 className="text-4xl font-bold text-white mb-2">Live Stream</h1>
        <p className="text-gray-400 mb-8">Create and manage live battle streams</p>

        {!streamData ? (
          <div className="bg-gray-900 rounded-lg p-8 text-center">
            <h2 className="text-2xl text-white font-bold mb-4">Start a New Live Stream</h2>
            <p className="text-gray-400 mb-6">
              Create a live stream to broadcast a battle event. You will get stream credentials for OBS or Streamyard.
            </p>
            <button
              onClick={handleCreateStream}
              disabled={creating}
              className="bg-red-600 hover:bg-red-700 disabled:bg-gray-700 text-white font-bold py-4 px-12 rounded-lg transition-colors text-lg"
            >
              {creating ? 'Creating...' : 'Create Live Stream'}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
              <p className="text-green-400 font-medium">Live stream created successfully!</p>
            </div>

            {/* Stream Credentials */}
            <div className="bg-gray-900 rounded-lg p-6 space-y-4">
              <h3 className="text-xl text-white font-bold">Stream Credentials (for OBS/Streamyard)</h3>

              <div>
                <label className="block text-gray-400 text-sm mb-1">RTMPS URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={streamData.rtmps_url}
                    className="flex-1 bg-gray-800 border border-gray-700 text-white rounded px-3 py-2 text-sm font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(streamData.rtmps_url, 'RTMPS URL')}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-1">Stream Key</label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    readOnly
                    value={streamData.stream_key}
                    className="flex-1 bg-gray-800 border border-gray-700 text-white rounded px-3 py-2 text-sm font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(streamData.stream_key, 'Stream Key')}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-1">Playback ID</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={streamData.playback_id}
                    className="flex-1 bg-gray-800 border border-gray-700 text-white rounded px-3 py-2 text-sm font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(streamData.playback_id, 'Playback ID')}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-gray-900 rounded-lg p-6">
              <h3 className="text-xl text-white font-bold mb-4">How to Go Live</h3>
              <ol className="space-y-3 text-gray-300">
                <li className="flex gap-3">
                  <span className="text-red-500 font-bold">1.</span>
                  Open OBS Studio or Streamyard
                </li>
                <li className="flex gap-3">
                  <span className="text-red-500 font-bold">2.</span>
                  Set the server to the RTMPS URL above
                </li>
                <li className="flex gap-3">
                  <span className="text-red-500 font-bold">3.</span>
                  Paste the Stream Key
                </li>
                <li className="flex gap-3">
                  <span className="text-red-500 font-bold">4.</span>
                  Start streaming -- viewers with access will see it on the site
                </li>
                <li className="flex gap-3">
                  <span className="text-red-500 font-bold">5.</span>
                  After the stream ends, it will automatically be saved as a VOD
                </li>
              </ol>
            </div>

            <button
              onClick={() => setStreamData(null)}
              className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-8 rounded-lg transition-colors"
            >
              Create Another Stream
            </button>
          </div>
        )}

        <AlertModal
          show={alertModal.show}
          title={alertModal.title}
          message={alertModal.message}
          type={alertModal.type}
          onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
        />
      </div>
    </div>
  );
};

export default LiveStreamAdmin;
